import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IActionSweepMemberAdd, IFloorPlanDelete, IFloorPlanDeleteFailure, IFloorPlanSaveFailure, IFloorPlanSaveRequest, IFloorPlanSaveSuccess, IFrequencyAnalysisDelete, IFrequencyAnalysisDeleteFailure, IFrequencyAnalysisSaveFailure, IFrequencyAnalysisSaveRequest, IFrequencyAnalysisSaveSuccess, IFrequencyFileDelete, IFrequencyFileDeleteFailure, IFrequencyFileSaveFailure, IFrequencyFileSaveRequest, IFrequencyFileSaveSuccess, IPaginations, IRoomsError, IRoomWorkRegisterRemoveFailureActionPayload, ISweep, ISweepBasic, ISweepGuestRemoveFailureActionPayload, ISweepMember, ISweepPagination, ISweepRemoveFailureActionPayload, ISweepRoom, ISweepRoomItem, ISweepRoomItemRemoveFailureActionPayload, ISweepRoomRemoveFailureActionPayload, ISweepRoomTime, ISweepsError, ISweepsFailure, ISweepsState, ISweepTime, ISweepWorkRegisterRemoveFailureActionPayload } from './types'


const initialState: ISweepsState = {
  isLoading: false,
  data: [],
  pagination: {
    count: 0,
    limit: 0,
    currentPage: 1,
    totalPages: 1,
  },
}

export const sweepsSlice = createSlice({
  name: 'sweeps',
  initialState,
  reducers: {
    sweepsListRequest: (state, action: PayloadAction<IPaginations>) => {
      state.isLoading = true
      state.error = undefined;
    },
    sweepsListSuccess: (state, action: PayloadAction<{
      isArchived?: boolean;
      data: ISweep[]
    }>) => {
      const { data, isArchived } = action.payload;

      state.isLoading = false;

      const sweepsToKeep = state.data.filter(sweep => (isArchived && !sweep.archivedAt) || (!isArchived && sweep.archivedAt));

      state.data = [...sweepsToKeep, ...data]

      state.error = undefined;
    },
    sweepsListFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    sweepArchivedPagination: (state, action: PayloadAction<ISweepPagination>) => {
      state.pagination = action.payload

    },
    startEditingSweep: (state, action: PayloadAction<ISweep>) => {
      state.draftSweep = action.payload;
    },
    stopEditingSweep: (state) => {
      state.draftRoom = undefined;
    },
    sweepDraftUpdate: (state, action: PayloadAction<ISweep>) => {
      state.draftSweep = action.payload;
    },
    sweepsSaveRequest: (state, action: PayloadAction<ISweep>) => {
      if (state.draftSweep) {
        state.draftSweep.isSaving = true;
      }
    },
    sweepsSaveSuccess: (state, action: PayloadAction<ISweep>) => {
      const oldSweep = state.data.find(sweep => sweep.id === action.payload.id)

      if (!oldSweep) {
        state.data.push({
          ...action.payload,
          error: undefined,
          rooms: [],
          members: [],
          times: [],
        })
      }
      else {
        state.data = state.data.map(sweep => {
          if (sweep.id !== action.payload.id) return sweep;

          return {
            ...sweep,
            ...action.payload,
            isSaving: false,
            error: undefined
          }
        })
      }

      state.draftSweep = undefined;
    },
    sweepsSaveFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      if (state.draftSweep) {
        state.draftSweep.isSaving = false
        state.draftSweep.error = action.payload.error
      }
    },
    sweepRemoveRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload) return sweep;

        return {
          ...sweep,
          isDeleting: true
        };
      })
    },
    sweepRemoveSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter(sweep => sweep.id !== action.payload)
    },
    sweepRemoveFailure: (state, action: PayloadAction<ISweepRemoveFailureActionPayload>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          isDeleting: false,
          error: action.payload.error
        };
      })
    },
    sweepExport: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload) return sweep;

        return {
          ...sweep,
          isExporting: true
        };
      })
    },
    sweepExportSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload) return sweep;

        return {
          ...sweep,
          isExporting: false
        };
      })
    },
    sweepExportFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.id) return sweep;

        return {
          ...sweep,
          isExporting: false,
          error: action.payload.error
        };
      })
    },
    sweepArchive: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload) return sweep;

        return {
          ...sweep,
          isTogglingArchive: true
        };
      })
    },
    sweepArchiveSuccess: (state, action: PayloadAction<ISweepBasic>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.id) return sweep;

        return {
          ...sweep,
          ...action.payload,
          isTogglingArchive: false
        };
      })
    },
    sweepArchiveFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.id) return sweep;

        return {
          ...sweep,
          isTogglingArchive: false,
          error: action.payload.error
        };
      })
    },
    sweepUnarchive: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload) return sweep;

        return {
          ...sweep,
          isTogglingArchive: true
        };
      })
    },
    sweepUnarchiveSuccess: (state, action: PayloadAction<ISweepBasic>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.id) return sweep;

        return {
          ...sweep,
          ...action.payload,
          isTogglingArchive: false
        };
      })
    },
    sweepUnarchiveFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.id) return sweep;

        return {
          ...sweep,
          isTogglingArchive: false,
          error: action.payload.error
        };
      })
    },
    // MEMBERS
    sweepAddMember: (state, action: PayloadAction<IActionSweepMemberAdd>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.idSweep) return sweep;

        return {
          ...sweep,
          members: [
            ...sweep.members,
            {
              id: action.payload.id || "",
              idUser: action.payload.idUser,
              name: action.payload.name,
              role: action.payload.role || "",
              sweepId: sweep.id || ""
            }
          ]
        }
      })
    },
    startEditingSweepMember: (state, action: PayloadAction<ISweepMember>) => {
      state.draftMember = action.payload;
    },
    stopEditingSweepMember: (state) => {
      state.draftRoom = undefined;
    },
    sweepMemberDraftUpdate: (state, action: PayloadAction<ISweepMember>) => {
      state.draftMember = action.payload;
      state.draftMember.error = undefined;
    },
    sweepsSaveGuestRequest: (state, action: PayloadAction<ISweepMember>) => {
      if (state.draftMember) {
        state.draftMember.isSaving = true;
        state.draftMember.error = undefined;
      }
      state.error = undefined;
    },
    sweepsSaveGuestSuccess: (state, action: PayloadAction<ISweepMember>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          members: [
            ...sweep.members.filter(member => member.id !== action.payload.id),
            action.payload,
          ],
          isSaving: false,
          error: undefined,
        }
      })
    },
    sweepsSaveGuestFailure: (state, action: PayloadAction<ISweepsError>) => {
      if (state.draftMember) {
        state.draftMember.error = action.payload.error.message;
        state.draftMember.isSaving = false;
      }
    },
    sweepGuestRemoveRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.members = sweep.members.map(member => {
          if (member.id !== action.payload) return member;

          return {
            ...member,
            isDeleting: true,
            error: undefined
          }
        })

        return sweep;
      })
    },
    sweepGuestRemoveSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.members = sweep.members.filter(member => member.id !== action.payload)

        return sweep;
      })
    },
    sweepGuestRemoveFailure: (state, action: PayloadAction<ISweepGuestRemoveFailureActionPayload>) => {
      state.data = state.data.map(sweep => {
        sweep.members = sweep.members.map(member => {
          if (member.id !== action.payload.memberId) return member;

          return {
            ...member,
            isDeleting: false,
            error: action.payload.error
          }
        })

        return sweep;
      })
    },
    // ROOMS
    startEditingSweepRoom: (state, action: PayloadAction<ISweepRoom>) => {
      state.draftRoom = action.payload;
    },
    stopEditingSweepRoom: (state) => {
      state.draftRoom = undefined;
    },
    sweepRoomDraftUpdate: (state, action: PayloadAction<ISweepRoom>) => {
      state.draftRoom = action.payload;
    },
    sweepsSaveRoomRequest: (state, action: PayloadAction<ISweepRoom>) => {
      if (state.draftRoom) {
        state.draftRoom.isSaving = true;
        state.draftRoom.error = undefined;
      }
    },
    sweepsSaveRoomSuccess: (state, action: PayloadAction<ISweepRoom>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          isSaving: false,
          rooms: [
            ...sweep.rooms.filter(room => room.id !== action.payload.id),
            action.payload
          ].sort((a, b) => a.name.localeCompare(b.name)),
          error: undefined
        }
      })
    },
    sweepsSaveRoomFailure: (state, action: PayloadAction<ISweepsError>) => {
      if (state.draftRoom) {
        state.draftRoom.error = action.payload;
        state.draftRoom.isSaving = false;
      }
    },
    sweepRoomRemoveRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          if (room.id !== action.payload) return room;

          return {
            ...room,
            isDeleting: true,
            error: undefined
          }
        })

        return sweep;
      })
    },
    sweepRoomRemoveSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.filter(room => room.id !== action.payload)

        return sweep;
      })
    },
    sweepRoomRemoveFailure: (state, action: PayloadAction<ISweepRoomRemoveFailureActionPayload>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          if (room.id !== action.payload.roomId) return room;

          return {
            ...room,
            isDeleting: false,
            error: action.payload.error
          }
        })

        return sweep;
      })
    },
    roomExportPhotosRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload) return room;

            return {
              ...room,
              isExportingPhotos: true,
              error: undefined
            }
          })
        };
      })
    },
    roomExportPhotosSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload) return room;

            return {
              ...room,
              isExportingPhotos: false,
              error: undefined
            }
          })
        };
      })
    },
    roomExportPhotosFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.id) return room;

            return {
              ...room,
              isExportingPhotos: false,
              error: action.payload.error
            }
          })
        };
      })
    },
    roomExportPhotosFlirRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload) return room;

            return {
              ...room,
              isExportingFlir: true,
              error: undefined
            }
          })
        };
      })
    },
    roomExportPhotosFlirSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload) return room;

            return {
              ...room,
              isExportingFlir: false,
              error: undefined
            }
          })
        };
      })
    },
    roomExportPhotosFlirFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.id) return room;

            return {
              ...room,
              isExportingFlir: false,
              error: action.payload.error
            }
          })
        };
      })
    },
    // FLOOR PLAN *** SAVE ***
    roomFloorPlanSaveRequest(state, action: PayloadAction<IFloorPlanSaveRequest>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId && action.payload.uploadId) return room;

            return {
              ...room,
              isSavingFloorPlan: true,
              error: undefined,
            }
          })
        }
      })
    },
    roomFloorPlanSaveSuccess(state: ISweepsState, action: PayloadAction<IFloorPlanSaveSuccess>) {

      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isSavingFloorPlan: false,
              floorPlan: action.payload.floorPlan,
              error: undefined
            }
          })
        }
      })
    },
    roomFloorPlanSaveFailure(state, action: PayloadAction<IFloorPlanSaveFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isSavingFloorPlan: false,
              error: action.payload.error
            }
          })
        }
      })
    },
    //FLOR PLAN *** DELETE ***
    roomFloorPlanDeleteRequest(state, action: PayloadAction<IFloorPlanDelete>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFloorPlan: true,
            }
          }),
          error: undefined
        }
      })
    },
    roomFloorPlanDeleteSuccess: (state, action: PayloadAction<IFloorPlanDelete>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFloorPlan: false,
              floorPlan: undefined,
              error: undefined,
            }
          })
        }
      })
    },
    roomFloorPlanDeleteFailure(state, action: PayloadAction<IFloorPlanDeleteFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFloorPlan: false
            }
          }),
          error: action.payload.error,
        }
      })
    },
    // FREQUENCY FILE *** SAVE ***
    roomFrequencyFileSaveRequest(state, action: PayloadAction<IFrequencyFileSaveRequest>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId && action.payload.uploadId) return room;

            return {
              ...room,
              isSavingFrequencyFile: true,
              error: undefined,
            }
          })
        }
      })
    },
    roomFrequencyFileSaveSuccess(state: ISweepsState, action: PayloadAction<IFrequencyFileSaveSuccess>) {

      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isSavingFrequencyFile: false,
              frequencyFile: action.payload.frequencyFile,
              error: undefined
            }
          })
        }
      })
    },
    roomFrequencyFileSaveFailure(state, action: PayloadAction<IFrequencyFileSaveFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isSavingFrequencyFile: false,
              error: action.payload.error
            }
          })
        }
      })
    },
    //FREQUENCY FILE *** DELETE ***
    roomFrequencyFileDeleteRequest(state, action: PayloadAction<IFrequencyFileDeleteFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFrequencyFile: true,
            }
          }),
          error: undefined
        }
      })
    },
    roomFrequencyFileDeleteSuccess: (state, action: PayloadAction<IFrequencyFileDelete>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFrequencyFile: false,
              frequencyFile: undefined,
              error: undefined,
            }
          })
        }
      })
    },
    roomFrequencyFileDeleteFailure(state, action: PayloadAction<IFrequencyFileDeleteFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFrequencyFile: false
            }
          }),
          error: action.payload.error,
        }
      })
    },
    // ROOM ITEMS
    startEditingSweepRoomItem: (state, action: PayloadAction<ISweepRoomItem>) => {
      state.draftRoomItem = action.payload;
    },
    stopEditingSweepRoomItem: (state) => {
      state.draftRoomItem = undefined;
    },
    sweepRoomItemDraftUpdate: (state, action: PayloadAction<ISweepRoomItem>) => {
      state.draftRoomItem = action.payload;
    },
    sweepsSaveRoomItemRequest: (state, action: PayloadAction<ISweepRoomItem>) => {
      if (state.draftRoomItem) {
        state.draftRoomItem.isSaving = true;
        state.draftRoomItem.error = undefined;
      }
    },
    sweepsSaveRoomItemSuccess: (state, action: PayloadAction<ISweepRoomItem>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          isSaving: false,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              items: [
                ...room.items.filter(item => item.id !== action.payload.id),
                action.payload
              ].sort((a, b) => a.name.localeCompare(b.name))
            }
          })
        }
      })

      state.draftRoomItem = undefined;
    },
    sweepsSaveRoomItemFailure: (state, action: PayloadAction<ISweepsError>) => {
      if (state.draftRoomItem) {
        state.draftRoomItem.error = action.payload;
        state.draftRoomItem.isSaving = false;
      }
    },
    sweepRoomItemRemoveRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          return {
            ...room,
            items: room.items.map(item => {
              if (item.id !== action.payload) return item;

              return {
                ...item,
                isDeleting: true,
                error: undefined
              }
            })
          }
        })

        return sweep;
      })
    },
    sweepRoomItemRemoveSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          room.items = room.items.filter(item => item.id !== action.payload)

          return room;
        })

        return sweep;
      })
    },
    sweepRoomItemRemoveFailure: (state, action: PayloadAction<ISweepRoomItemRemoveFailureActionPayload>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          return {
            ...room,
            items: room.items.map(item => {
              if (item.id !== action.payload.itemId) return item;

              return {
                ...item,
                isDeleting: false,
                error: action.payload.error
              }
            })
          }
        })
        return sweep;
      })
    },
    roomExportVideosRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload) return room;

            return {
              ...room,
              isExportingVideos: true,
              error: undefined
            }
          })
        };
      })
    },
    roomExportVideosSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload) return room;

            return {
              ...room,
              isExportingVideos: false,
              error: undefined
            }
          })
        };
      })
    },
    roomExportVideosFailure: (state, action: PayloadAction<ISweepsFailure>) => {
      state.data = state.data.map(sweep => {
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.id) return room;

            return {
              ...room,
              isExportingVideos: false,
              error: action.payload.error
            }
          })
        };
      })
    },

    // *** FREQUENCY ANALYSIS | UPLOAD ***

    roomFrequencyAnalysisSaveRequest(state, action: PayloadAction<IFrequencyAnalysisSaveRequest>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId && action.payload.uploadId) return room;

            return {
              ...room,
              isSavingFrequencyAnalysis: true,
              error: undefined,
            }
          })
        }
      })
    },
    roomFrequencyAnalysisSaveSuccess(state: ISweepsState, action: PayloadAction<IFrequencyAnalysisSaveSuccess>) {

      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isSavingFrequencyAnalysis: false,
              frequencyAnalysis: action.payload.frequencyAnalysis,
              error: undefined
            }
          })
        }
      })
    },
    roomFrequencyAnalysisSaveFailure(state, action: PayloadAction<IFrequencyAnalysisSaveFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isSavingFrequencyAnalysis: false,
              error: action.payload.error
            }
          })
        }
      })
    },

    // *** FREQUENCY ANALYSIS | DELETE ***

    roomFrequencyAnalysisDeleteRequest(state, action: PayloadAction<IFrequencyAnalysisDelete>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;
        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFrequencyAnalysis: true,
            }
          }),
          error: undefined
        }
      })
    },
    roomFrequencyAnalysisDeleteSuccess: (state, action: PayloadAction<IFrequencyAnalysisDelete>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFrequencyAnalysis: false,
              frequencyAnalysis: undefined,
              error: undefined,
            }
          })
        }
      })
    },
    roomFrequencyAnalysisDeleteFailure(state, action: PayloadAction<IFrequencyAnalysisDeleteFailure>) {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              isDeletingFrequencyAnalysis: false
            }
          }),
          error: action.payload.error,
        }
      })
    },
    // *** SWEEP WORK REGISTER || ADD 
    startEditingRegister: (state, action: PayloadAction<ISweepTime>) => {
      state.draftWorkSweepRegister = action.payload;
    },
    sweepWorkRegisterDraftUpdate: (state, action: PayloadAction<ISweepTime>) => {
      state.draftWorkSweepRegister = action.payload;
    },
    sweepsSaveWorkRegisterRequest: (state, action: PayloadAction<ISweepTime>) => {
      if (state.draftWorkSweepRegister) {
        state.draftWorkSweepRegister.isSaving = true;
      }
    },
    sweepsSaveWorkRegisterSuccess: (state, action: PayloadAction<ISweepTime>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          times: [
            ...sweep.times.filter(times => times.id !== action.payload.id),
            action.payload,
          ],
          isSaving: false,
          error: undefined,
        }
      })
      state.draftWorkSweepRegister = undefined;
    },
    sweepsSaveWorkRegisterFailure: (state, action: PayloadAction<ISweepsError>) => {
      if (state.draftWorkSweepRegister) {
        state.draftWorkSweepRegister.isSaving = false;
        state.draftWorkSweepRegister.error = action.payload.error
      }
    },
    // *** SWEEP WORK REGISTER || REMOVE
    sweepWorkRegisterRemoveRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.times = sweep.times.map(timer => {
          if (timer.id !== action.payload) return timer;

          return {
            ...timer,
            isDeleting: true,
            error: undefined
          }
        })

        return sweep;
      })
    },
    sweepWorkRegisterRemoveSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.times = sweep.times.filter(timer => timer.id !== action.payload)

        return sweep;
      })
    },
    sweepWorkRegisterRemoveFailure: (state, action: PayloadAction<ISweepWorkRegisterRemoveFailureActionPayload>) => {
      state.data = state.data.map(sweep => {
        sweep.times = sweep.times.map(timer => {
          if (timer.id !== action.payload.workRegisterId) return timer;

          return {
            ...timer,
            isDeleting: false,
            error: action.payload.error
          }
        })

        return sweep;
      })
    },
    // *** ROOM WORK REGISTER || ADD 
    startEditingRoomRegister: (state, action: PayloadAction<ISweepRoomTime>) => {
      state.draftWorkRoomRegister = action.payload;
    },
    roomWorkRegisterDraftUpdate: (state, action: PayloadAction<ISweepRoomTime>) => {
      state.draftWorkRoomRegister = action.payload;
    },
    roomSaveWorkRegisterRequest: (state, action: PayloadAction<ISweepRoomTime>) => {
      if (state.draftWorkRoomRegister) {
        state.draftWorkRoomRegister.isSaving = true;
      }
    },
    roomSaveWorkRegisterSuccess: (state, action: PayloadAction<ISweepRoomTime>) => {
      state.data = state.data.map(sweep => {
        if (sweep.id !== action.payload.sweepId) return sweep;

        return {
          ...sweep,
          rooms: sweep.rooms.map(room => {
            if (room.id !== action.payload.roomId) return room;

            return {
              ...room,
              times: [
                ...room.times.filter(times => times.id !== action.payload.id),
                action.payload,
              ],
              isSaving: false,
              error: undefined,
            }
          })
        }
      })
      state.draftWorkRoomRegister = undefined;
    },
    roomSaveWorkRegisterFailure: (state, action: PayloadAction<IRoomsError>) => {
      if (state.draftWorkRoomRegister) {
        state.draftWorkRoomRegister.isSaving = false;
        state.draftWorkRoomRegister.error = action.payload.error
      }
    },
    // *** ROOM WORK REGISTER || REMOVE

    roomWorkRegisterRemoveRequest: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          room.times = room.times.filter(timer => {
            if (timer.id !== action.payload) return timer;

            return {
              ...timer,
              isDeleting: true,
              error: undefined
            }
          })
          return room;
        })
        return sweep;
      })
    },
    roomWorkRegisterRemoveSuccess: (state, action: PayloadAction<string>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          room.times = room.times.filter(timer => (timer.id !== action.payload))
          return room;
        })
        return sweep;
      })
    },
    roomWorkRegisterRemoveFailure: (state, action: PayloadAction<IRoomWorkRegisterRemoveFailureActionPayload>) => {
      state.data = state.data.map(sweep => {
        sweep.rooms = sweep.rooms.map(room => {
          room.times = room.times.map(timer => {
            if (timer.id !== action.payload.workRegisterId) return timer;

            return {
              ...timer,
              isDeleting: false,
              error: action.payload.error
            }
          })
          return room;
        })
        return sweep;
      })
    },
  }
})

// Action creators are generated for each case reducer function
export const {
  sweepsListRequest,
  sweepsListSuccess,
  sweepsListFailure,
  sweepArchivedPagination,
  startEditingSweep,
  stopEditingSweep,
  sweepDraftUpdate,
  sweepsSaveRequest,
  sweepsSaveSuccess,
  sweepsSaveFailure,
  sweepExport,
  sweepExportSuccess,
  sweepExportFailure,
  sweepAddMember,
  startEditingSweepMember,
  stopEditingSweepMember,
  sweepsSaveGuestRequest,
  sweepsSaveGuestSuccess,
  sweepsSaveGuestFailure,
  sweepGuestRemoveRequest,
  sweepGuestRemoveSuccess,
  sweepGuestRemoveFailure,
  sweepMemberDraftUpdate,
  sweepsSaveRoomRequest,
  sweepsSaveRoomSuccess,
  sweepsSaveRoomFailure,
  startEditingSweepRoom,
  stopEditingSweepRoom,
  sweepRoomDraftUpdate,
  sweepRoomRemoveRequest,
  sweepRoomRemoveSuccess,
  sweepRoomRemoveFailure,
  roomExportPhotosRequest,
  roomExportPhotosSuccess,
  roomExportPhotosFailure,
  roomExportPhotosFlirRequest,
  roomExportPhotosFlirSuccess,
  roomExportPhotosFlirFailure,
  roomFloorPlanSaveRequest,
  roomFloorPlanSaveSuccess,
  roomFloorPlanSaveFailure,
  roomFloorPlanDeleteRequest,
  roomFloorPlanDeleteSuccess,
  roomFloorPlanDeleteFailure,
  roomFrequencyFileSaveRequest,
  roomFrequencyFileSaveSuccess,
  roomFrequencyFileSaveFailure,
  roomFrequencyFileDeleteRequest,
  roomFrequencyFileDeleteSuccess,
  roomFrequencyFileDeleteFailure,
  sweepRemoveRequest,
  sweepRemoveSuccess,
  sweepRemoveFailure,
  startEditingSweepRoomItem,
  stopEditingSweepRoomItem,
  sweepRoomItemDraftUpdate,
  sweepsSaveRoomItemRequest,
  sweepsSaveRoomItemSuccess,
  sweepsSaveRoomItemFailure,
  sweepRoomItemRemoveRequest,
  sweepRoomItemRemoveSuccess,
  sweepRoomItemRemoveFailure,
  sweepArchive,
  sweepArchiveSuccess,
  sweepArchiveFailure,
  sweepUnarchive,
  sweepUnarchiveSuccess,
  sweepUnarchiveFailure,
  roomExportVideosRequest,
  roomExportVideosSuccess,
  roomExportVideosFailure,
  roomFrequencyAnalysisSaveRequest,
  roomFrequencyAnalysisSaveSuccess,
  roomFrequencyAnalysisSaveFailure,
  roomFrequencyAnalysisDeleteRequest,
  roomFrequencyAnalysisDeleteSuccess,
  roomFrequencyAnalysisDeleteFailure,
  sweepsSaveWorkRegisterRequest,
  sweepsSaveWorkRegisterSuccess,
  sweepsSaveWorkRegisterFailure,
  sweepWorkRegisterDraftUpdate,
  startEditingRegister,
  sweepWorkRegisterRemoveRequest,
  sweepWorkRegisterRemoveSuccess,
  sweepWorkRegisterRemoveFailure,
  roomSaveWorkRegisterRequest,
  roomSaveWorkRegisterSuccess,
  roomSaveWorkRegisterFailure,
  roomWorkRegisterDraftUpdate,
  startEditingRoomRegister,
  roomWorkRegisterRemoveRequest,
  roomWorkRegisterRemoveSuccess,
  roomWorkRegisterRemoveFailure,
} = sweepsSlice.actions

export default sweepsSlice.reducer